/**
 * conversationBurst (Seth 2026-06-26) — when we summarize a text/chat thread
 * into a dispatch, summarize the WHOLE back-and-forth, not one message. Podium
 * keeps a single forever-thread per customer, so "everything" would drag in
 * months of unrelated review-blasts; instead we scope to the CURRENT burst:
 * walk back from the newest message and stop at a multi-day gap of silence
 * (that gap = a previous, unrelated episode). Both directions, speaker-labeled,
 * so the AI sees the customer's symptoms AND what we promised (gate codes,
 * windows). Transparent — we tell the dispatcher how many messages we used.
 */

export interface BurstMsg {
  direction: string; // "inbound" | "outbound"
  channel?: string | null;
  body?: string | null;
  created_at: string;
}

/** Days of silence that separate one conversation episode from the next. */
const GAP_DAYS = 3;

/** The current contiguous run of real messages (no internal gap > GAP_DAYS),
 *  ending at the newest. Notes and empty bodies are ignored. */
export function currentBurst<T extends BurstMsg>(messages: T[], gapDays = GAP_DAYS): T[] {
  const real = messages
    .filter((m) => m.channel !== "note" && (m.body ?? "").trim().length > 0)
    .slice()
    .sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at));
  if (real.length === 0) return [];
  const gapMs = gapDays * 24 * 60 * 60 * 1000;
  let start = real.length - 1;
  for (let i = real.length - 1; i > 0; i--) {
    const gap = +new Date(real[i].created_at) - +new Date(real[i - 1].created_at);
    if (gap > gapMs) break; // older message belongs to a previous episode
    start = i - 1;
  }
  return real.slice(start);
}

/** Speaker-labeled transcript the AI reads ("Customer:" / "Us:"). */
export function buildTranscript(messages: BurstMsg[]): string {
  return messages
    .map((m) => `${m.direction === "inbound" ? "Customer" : "Us"}: ${(m.body ?? "").trim()}`)
    .join("\n");
}

/** "6 messages from today" / "4 messages from Jun 24" / "5 messages over 3 days"
 *  — shown to the dispatcher so they can see exactly what fed the summary. */
export function burstScopeLabel(messages: BurstMsg[]): string {
  const n = messages.length;
  if (n === 0) return "no messages";
  const noun = `${n} message${n === 1 ? "" : "s"}`;
  const first = new Date(messages[0].created_at);
  const last = new Date(messages[messages.length - 1].created_at);
  const sameDay = first.toDateString() === last.toDateString();
  if (sameDay) {
    const when =
      first.toDateString() === new Date().toDateString()
        ? "today"
        : first.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${noun} from ${when}`;
  }
  const days =
    Math.round(
      (+new Date(last.toDateString()) - +new Date(first.toDateString())) /
        (24 * 60 * 60 * 1000),
    ) + 1;
  return `${noun} over ${days} days`;
}

/** True when the burst contains at least one inbound (customer) message —
 *  nothing to summarize from an outbound-only review blast. */
export function hasInbound(messages: BurstMsg[]): boolean {
  return messages.some((m) => m.direction === "inbound");
}
